/**
 * Interactive Survey Market Map Component
 *
 * This component provides an interactive map interface for viewing survey properties
 * using Mapbox GL JS for high-performance vector maps.
 *
 * MAPBOX CONFIGURATION:
 * - Requires a valid Mapbox access token in VITE_MAPBOX_TOKEN environment variable
 * - For development, a fallback public token is used if the environment token is invalid
 */

import { useAppDispatch, useAppSelector } from "@/app/hooks";
import MapSettingsIcon from "@/assets/images/map-settings-siteiq.svg?react";
import { updateMapFilters } from "@/features/auth/interactiveSurveySlice";
import { LeaseComp, LeaseCompFieldRule } from "@/features/leaseComps/types";
import { POI } from "@/features/poi/types";
import { Property } from "@/features/survey/types";
import PropertyImagesCache from "@/shared/components/PropertyImagesCache";
import {
  getPropertyMarkerContent,
  hasAddressLine2AsCustom,
} from "@/shared/utils";
import { MAP_STYLES, MapStyle } from "@/shared/utils/constants";
import {
  createLeaseCompMarkerElement,
  createPoiMarkerElement,
  createPropertyMarkerSvgElement,
  filterLeaseComps,
  filterPois,
  filterPropertiesForMap,
  getIconData,
  getLeaseCompCoordinates,
  getLeaseCompIconData,
  getPoiCoordinates,
  getPoiIconData,
  isEmptyObjectFromStr,
  makeMapKeyCompositeKey,
} from "@/shared/utils/map";
import { clearMarkers } from "@/shared/utils/mapMarkers";
import {
  addMarinePortLayer,
  centerMap as centerMapUtil,
  closeCurrentPopup,
  deleteMarkers,
  getCoordinates,
} from "@/shared/utils/mapUtils";
import { isFieldVisible } from "@/shared/utils/propertyFieldRules";
import {
  addRadiusCircle as addRadiusCircleUtil,
  clearAllRadiusCircles,
  removeRadiusCircle,
} from "@/shared/utils/radiusCircles";
import {
  addPolygonsToMap,
  removeAllPolygons,
} from "@/shared/utils/polygonUtils";
import { useGetPolygonsQuery } from "@/services/polygonService";
import { EmeraldCheckbox } from "@emerald-react/checkbox";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldPopover } from "@emerald-react/popover";
import { EmeraldRadio, EmeraldRadioGroup } from "@emerald-react/radio";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router";
import CenterImg from "./components/CenterImg";
import MapKey from "./components/MapKey";
import { createMapPopupElement } from "./components/MapPopupContent";
import PropertySidebar from "./components/PropertySidebar";

// Configure Mapbox access token
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

// Validate and set the access token
if (MAPBOX_TOKEN && MAPBOX_TOKEN.startsWith("pk.")) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
} else {
  console.warn(
    "Invalid or missing VITE_MAPBOX_TOKEN, using fallback token for demo purposes",
  );
}

// Loading state interface
interface LoadingState {
  map: boolean;
  properties: boolean;
  error: string | null;
}

const getPoiLocationNotes = (poi: POI): string | null => {
  const candidateFields = [
    poi.locationNotes,
    poi.locationNote,
    poi.notes,
    poi.location_notes,
  ];

  const notes = candidateFields.find(
    (value): value is string =>
      typeof value === "string" && value.trim().length > 0,
  );

  return notes?.trim() ?? null;
};

const getPoiAddress = (poi: POI): string | null => {
  const directAddress = poi.address?.trim();
  if (directAddress) return directAddress;

  const line1 = poi.addressLine1?.trim();
  const line2 = poi.addressLine2?.trim();
  const city = poi.city?.trim();
  const state = poi.state?.trim();
  const postalCode = poi.postalCode?.trim();

  const street = [line1, line2].filter(Boolean).join(", ");
  const locality = [city, state].filter(Boolean).join(", ");
  const address = [street, locality, postalCode].filter(Boolean).join(" ");

  return address.length > 0 ? address : null;
};

const InteractiveSurveyMarketMap = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const dispatch = useAppDispatch();
  const { currentSurvey, mapFilters, hiddenMapKeyColors } = useAppSelector(
    (state) => state.interactiveSurvey,
  );
  const { currentBingMap } = useAppSelector((state) => state.bingMap);
  const { marinePorts } = useAppSelector((state) => state.media);
  const isIncludeLeaseComps = currentSurvey?.isIncludeLeaseComps ?? false;
  const { leaseComps: rawLeaseComps, fieldRules: leaseCompFieldRules } =
    useAppSelector((state) => state.leaseComps);
  const leaseComps = isIncludeLeaseComps ? rawLeaseComps : [];
  const { leaseMediaFiles } = useAppSelector((state) => state.leaseMedia);
  const { pois: rawPois } = useAppSelector((state) => state.poi);
  const pois = rawPois;
  const isIncludePolygons = currentSurvey?.isIncludePolygons ?? true;
  const {
    data: polygons = [],
    isError: isPolygonError,
    isFetching: isPolygonFetching,
  } = useGetPolygonsQuery(surveyId || "", {
    skip: !surveyId || !isIncludePolygons,
  });
  const hasAddressLine2Custom = currentSurvey
    ? hasAddressLine2AsCustom(currentSurvey)
    : false;
  const marketMapData =
    currentSurvey?.marketMapBamData &&
    typeof JSON.parse(currentSurvey.marketMapBamData) === "object" &&
    !isEmptyObjectFromStr(currentSurvey.marketMapBamData)
      ? JSON.parse(currentSurvey.marketMapBamData)
      : null;

  // Map and UI refs
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const leaseCompMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const poiMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const currentPopupRef = useRef<mapboxgl.Popup | null>(null);
  const radiusCirclesRef = useRef<Set<string>>(new Set()); // Track property IDs with radius circles
  const radiusLabelMarkersRef = useRef<Map<string, mapboxgl.Marker>>(new Map()); // Track radius label markers
  const polygonIdsRef = useRef<string[]>([]);
  const polygonsRef = useRef<typeof polygons>(polygons);
  const leaseCompFieldRulesRef =
    useRef<LeaseCompFieldRule[]>(leaseCompFieldRules);
  leaseCompFieldRulesRef.current = leaseCompFieldRules;
  polygonsRef.current = polygons;

  // Loading and error states
  const [loadingState, setLoadingState] = useState<LoadingState>({
    map: true,
    properties: true,
    error: null,
  });

  // Map and UI state
  const [currentMapStyle, setCurrentMapStyle] = useState<MapStyle>(() => {
    // Use survey's marketMapStyle if available, otherwise default to first style
    if (currentSurvey?.marketMapStyle) {
      let currentStyle = currentSurvey.marketMapStyle;
      if (currentSurvey.marketMapStyle === "bingHybrid") {
        currentStyle = "emeraldBingHybrid";
      }
      if (currentSurvey.marketMapStyle === "esriHybrid") {
        currentStyle = "emeraldEsriHybrid";
      }
      const surveyStyle = MAP_STYLES.find((style) => style.id === currentStyle);
      if (surveyStyle) {
        if (surveyStyle.id === "emeraldBingHybrid") {
          surveyStyle.url = currentBingMap || "";
        }
        return surveyStyle;
      }
    }
    return MAP_STYLES[0];
  });
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null,
  );
  const [selectedLeaseCompId, setSelectedLeaseCompId] = useState<string | null>(
    null,
  );
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [portsVisible, setPortsVisible] = useState<boolean>(false);

  // Property images cache for popup generation
  const [propertyImagesCache, setPropertyImagesCache] = useState<
    Record<string, string>
  >({});

  // Use Redux state for filters instead of local state
  const filters = mapFilters;

  // Properties data from Redux state
  const properties = useMemo(() => {
    const { showShortlistOnly } = filters;
    return showShortlistOnly
      ? currentSurvey?.properties?.filter(
          (p) => !p.isHidden && p.isClientShortlist,
        ) || []
      : currentSurvey?.properties?.filter((p) => !p.isHidden) || [];
  }, [currentSurvey?.properties, filters]);

  useEffect(() => {
    if (!marinePorts) return;
    fetchGeoJson();
  }, [marinePorts]);

  const fetchGeoJson = async () => {
    try {
      setPortsVisible(currentSurvey?.isMarinePorts ?? false);
      if (!map.current) return;

      const addLayer = () => {
        if (map.current) {
          addMarinePortLayer(
            map.current,
            marinePorts || [],
            currentSurvey?.isMarinePorts ?? false,
          );
        }
      };

      if (map.current.isStyleLoaded()) {
        addLayer();
      } else {
        map.current.once("idle", addLayer);
      }
    } catch (error) {
      console.error("Failed to fetch marine ports data", error);
    }
  };

  // Set properties loading state based on survey data availability
  useEffect(() => {
    if (currentSurvey) {
      setLoadingState((prev) => ({ ...prev, properties: false }));

      // Update map style if survey has a preferred marketMapStyle
      if (currentSurvey.marketMapStyle) {
        let currentStyle = currentSurvey.marketMapStyle;
        if (currentSurvey.marketMapStyle === "bingHybrid") {
          currentStyle = "emeraldBingHybrid";
        }
        if (currentSurvey.marketMapStyle === "esriHybrid") {
          currentStyle = "emeraldEsriHybrid";
        }
        const surveyStyle = MAP_STYLES.find(
          (style) => style.id === currentStyle,
        );
        if (surveyStyle && surveyStyle.id !== currentMapStyle.id) {
          // Directly set currentMapStyle to avoid dependency issues
          setCurrentMapStyle(surveyStyle);
        }
      }
    } else {
      setLoadingState((prev) => ({ ...prev, properties: true }));
    }
  }, [currentSurvey]);

  // Filtered properties for sidebar - includes all properties that pass shortlist/hidden filters
  const sidebarProperties = useMemo(() => {
    return properties.filter((property) => {
      // Hidden filter
      if (property.isHidden) {
        return false;
      }
      // Shortlist filter
      if (filters.showShortlistOnly) {
        return property.isClientShortlist;
      }

      return true;
    });
  }, [properties, filters]);

  // Filtered lease comps for map display - excluding hidden map key categories
  const visibleLeaseComps = filterLeaseComps(leaseComps);
  const filteredLeaseComps = hiddenMapKeyColors.length
    ? visibleLeaseComps.filter((lc) => {
        const { iconColor } = getLeaseCompIconData(lc);
        return !hiddenMapKeyColors.includes(
          makeMapKeyCompositeKey(iconColor, false, "LeaseComp"),
        );
      })
    : visibleLeaseComps;

  // Filtered POIs for map display - excluding hidden map key categories
  const visiblePois = filterPois(pois);
  const filteredPois = hiddenMapKeyColors.length
    ? visiblePois.filter((poi) => {
        const { iconColor } = getPoiIconData(poi);
        // categoryId (extId) matches buildCompositeKey in MapKey; fall back to categoryName
        const categoryKey =
          poi.categoryId || poi.category?.categoryName || "POI";
        return !hiddenMapKeyColors.includes(
          makeMapKeyCompositeKey(iconColor, false, categoryKey),
        );
      })
    : visiblePois;

  // Filtered properties for map display - excluding hidden map key categories
  const visibleProperties = filterPropertiesForMap(sidebarProperties);
  const filteredProperties = hiddenMapKeyColors.length
    ? visibleProperties.filter((property) => {
        const { iconColor } = getIconData(property);
        return !hiddenMapKeyColors.includes(
          makeMapKeyCompositeKey(
            iconColor,
            property.isCurrentLocation ?? false,
            "Property",
          ),
        );
      })
    : visibleProperties;

  const renderPolygons = useCallback(() => {
    if (!map.current?.isStyleLoaded() || polygonsRef.current.length === 0) return;

    if (polygonIdsRef.current.length > 0) {
      removeAllPolygons(map.current, polygonIdsRef.current);
    }

    polygonIdsRef.current = addPolygonsToMap(map.current, polygonsRef.current);
  }, []);

  useEffect(() => {
    if (!isIncludePolygons || isPolygonError || isPolygonFetching || polygons.length === 0) return;
    if (map.current?.isStyleLoaded()) renderPolygons();
  }, [isPolygonError, isPolygonFetching, polygons.length, isIncludePolygons, renderPolygons]);

  // Remove polygons when isIncludePolygons becomes false
  useEffect(() => {
    if (!isIncludePolygons && map.current && polygonIdsRef.current.length > 0) {
      removeAllPolygons(map.current, polygonIdsRef.current);
      polygonIdsRef.current = [];
    }
  }, [isIncludePolygons]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    try {
      // Use the initial style but handle style changes through the separate changeMapStyle function
      const initialStyle = currentMapStyle.url;
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: initialStyle,
        center: marketMapData?.mapCentroid || [-97, 33], // NYC center
        projection: "mercator",
        zoom: marketMapData?.mapZoomLevel || 4,
        attributionControl: false,
      });

      // Add navigation controls
      map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

      // Map load event
      map.current.on("load", () => {
        addMarkersToMap();
        renderPolygons();
        setLoadingState((prev) => ({ ...prev, map: false }));
        if (marketMapData?.mapReset) {
          centerMapUtil(
            map.current,
            filteredProperties,
            currentSurvey?.isIncludeMapKey || false,
            [
              ...leaseComps,
              ...pois.map(
                (poi) =>
                  ({
                    leaseCompId: poi.poiId,
                    leaseCompFields: {
                      latitude: String(
                        poi.mapData?.latitude ?? poi.latitude ?? "",
                      ),
                      longitude: String(
                        poi.mapData?.longitude ?? poi.longitude ?? "",
                      ),
                    },
                  }) as unknown as LeaseComp,
              ),
            ],
            polygonsRef.current,
          );
        }
      });

      // Close popup when clicking on empty map areas
      map.current.on("click", (e) => {
        // Check if the click was on a marker or popup
        const features = map.current!.queryRenderedFeatures(e.point);
        const target = e.originalEvent.target as HTMLElement;
        const clickedOnMarker =
          target?.closest(".property-marker") ||
          target?.closest(".lease-comp-marker");
        const clickedOnPopup = target?.closest(".mapboxgl-popup");

        if (!clickedOnMarker && !clickedOnPopup) {
          setSelectedProperty(null);
          setSelectedLeaseCompId(null);
        }

        // Only close popup if not clicking on a marker or popup
        if (!clickedOnMarker && !clickedOnPopup && features.length === 0) {
          closeCurrentPopup(currentPopupRef);
        }
      });

      // Error handling
      map.current.on("error", (e) => {
        // Filter out non-critical source layer errors that don't affect functionality
        const errorMessage = e.error?.message || "";
        const isSourceLayerError = errorMessage.includes("Source layer") && 
                                   errorMessage.includes("does not exist");
        
        // Only log critical errors, suppress non-critical layer warnings
        if (!isSourceLayerError) {
          console.error("Map error:", e);
        }
      });
    } catch (error) {
      console.error("Failed to initialize map:", error);
      setLoadingState((prev) => ({
        ...prev,
        map: false,
        error:
          "Failed to initialize map. Please check your internet connection.",
      }));
    }

    // Cleanup
    return () => {
      if (map.current) {
        if (polygonIdsRef.current.length > 0) {
          removeAllPolygons(map.current, polygonIdsRef.current);
          polygonIdsRef.current = [];
        }
        map.current.remove();
        map.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array intentional - map is only initialized once

  useEffect(() => {
    if (!map.current || polygonsRef.current.length === 0) return;

    if (map.current.isStyleLoaded()) {
      renderPolygons();
    } else {
      map.current.once("idle", renderPolygons);
    }
  }, [renderPolygons, currentMapStyle.id, polygons.length]);

  // Radius circle helper functions - now using utility modules
  const addRadiusCircle = useCallback(
    (
      property: Property,
      radiusData: {
        radius?: { value: string | number }[];
        radiusLength?: string;
        radiusColor?: string;
        radiusLabel?: boolean;
      },
      radiusIndex: number,
    ) => {
      if (!map.current) return;

      addRadiusCircleUtil(
        map.current,
        property,
        radiusData,
        radiusIndex,
        getCoordinates,
        { radiusCirclesRef, radiusLabelMarkersRef },
      );
    },
    [],
  );

  const removeRadiusCircleWrapper = useCallback((propertyId: string) => {
    if (!map.current) return;
    removeRadiusCircle(map.current, propertyId, {
      radiusCirclesRef,
      radiusLabelMarkersRef,
    });
  }, []);

  const clearAllRadiusCirclesWrapper = useCallback(() => {
    if (!map.current) return;
    clearAllRadiusCircles(map.current, {
      radiusCirclesRef,
      radiusLabelMarkersRef,
    });
  }, []);

  // Clear markers helper
  const clearMarkersWrapper = useCallback(() => {
    clearMarkers({ markersRef, currentPopupRef }, setSelectedProperty);
  }, []);

  // Handle property click interactions
  const handlePropertyClick = useCallback((property: Property) => {
    // Close any existing popup first
    closeCurrentPopup(currentPopupRef);
    setSelectedLeaseCompId(null);
    setSelectedProperty(property);

    // Find the corresponding marker using the stored property ID and open its popup
    if (map.current && markersRef.current.length > 0) {
      const targetMarker = markersRef.current.find((marker) => {
        return (
          (marker as mapboxgl.Marker & { _propertyId: string })._propertyId ===
          property.id
        );
      });

      if (targetMarker) {
        const popup = targetMarker.getPopup();
        if (popup && !popup.isOpen()) {
          popup.addTo(map.current);
        }
      }
    }
  }, []);

  const handleLeaseCompClick = useCallback((leaseCompId: string) => {
    closeCurrentPopup(currentPopupRef);
    setSelectedProperty(null);
    setSelectedLeaseCompId(leaseCompId);

    // Find the corresponding lease comp marker and open its popup
    if (map.current && leaseCompMarkersRef.current.length > 0) {
      const targetMarker = leaseCompMarkersRef.current.find((marker) => {
        return (
          (marker as mapboxgl.Marker & { _leaseCompId: string })
            ._leaseCompId === leaseCompId
        );
      });

      if (targetMarker) {
        const popup = targetMarker.getPopup();
        if (popup && !popup.isOpen()) {
          popup.addTo(map.current);
        }
      }
    }
  }, []);

  // Update filter function - properly implemented to dispatch to Redux store
  const updateFilter = useCallback(
    (
      key: keyof typeof mapFilters,
      value: string | boolean | [number, number] | null,
    ) => {
      dispatch(updateMapFilters({ [key]: value }));
    },
    [dispatch],
  );

  // Helper function to get marker display content with shortlist index support
  const getMarkerDisplayContent = useCallback(
    (property: Property) => {
      // Otherwise use the original getPropertyMarkerContent function
      return getPropertyMarkerContent(property, properties);
    },
    [properties],
  );

  // Create property marker - temporarily keeping original due to type mismatch
  const createPropertyMarker = useCallback(
    (property: Property) => {
      const markerElement = document.createElement("div");
      markerElement.className = "property-marker";
      markerElement.setAttribute("role", "button");
      markerElement.setAttribute("tabindex", "0");
      markerElement.setAttribute(
        "aria-label",
        `Property: ${property.propertyName || property.name || property.address1}`,
      );
      // Parse bamMapData if available and extract iconColor and iconSize
      const bamData = getIconData(property);
      const iconColor = bamData.iconColor;
      const iconSize = bamData.iconSize;

      const displayContent = getMarkerDisplayContent(property);

      const innerDiv = createPropertyMarkerSvgElement(
        displayContent,
        iconColor,
        iconSize,
      );
      markerElement.appendChild(innerDiv);

      // Create popup content using DOM APIs to prevent XSS
      const cachedImageSrc = propertyImagesCache[property.id];
      const isPropertiesSectionHidden =
        currentSurvey?.sections?.find((s) => s.name === "properties")
          ?.isHidden ?? false;
      const hasFieldRules =
        !!currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList?.length;
      const popupElement = createMapPopupElement({
        property,
        surveyId: surveyId!,
        imageSrc: cachedImageSrc,
        showDetails: !isPropertiesSectionHidden,
        ...(!hasAddressLine2Custom
          ? {
              address2Visible: hasFieldRules
                ? isFieldVisible(currentSurvey, "address2")
                : true,
            }
          : {}),
        cityVisible: hasFieldRules
          ? isFieldVisible(currentSurvey, "city")
          : true,
        stateVisible: hasFieldRules
          ? isFieldVisible(currentSurvey, "stateProvince")
          : true,
        postalCodeVisible: hasFieldRules
          ? isFieldVisible(currentSurvey, "postalCode")
          : true,
      });

      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: true,
        maxWidth: "260px",
        className: "property-popup",
      }).setDOMContent(popupElement);

      // Wire up custom close button
      popup.on("open", () => {
        // Close any different popup that was previously open
        if (currentPopupRef.current && currentPopupRef.current !== popup) {
          currentPopupRef.current.remove();
        }
        currentPopupRef.current = popup;
        popupElement
          .querySelector(".popup-close-btn")
          ?.addEventListener("click", () => popup.remove());
      });

      // Add event listener for when popup closes
      popup.on("close", () => {
        if (currentPopupRef.current === popup) {
          currentPopupRef.current = null;
        }
      });

      const coords = getCoordinates(property);
      if (
        coords == null ||
        coords[0] == null ||
        coords[1] == null ||
        isNaN(coords[0]) ||
        isNaN(coords[1])
      ) {
        return null;
      }
      const marker = new mapboxgl.Marker(markerElement)
        .setLngLat(coords)
        .setPopup(popup);

      // Handle marker click for selection and popup
      const handleMarkerClick = () => {
        setSelectedLeaseCompId(null);
        setSelectedProperty(property);
        // Popup toggle is handled by Mapbox's setPopup; currentPopupRef is set via popup.on("open")
      };

      const handleKeyPress = (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleMarkerClick();
        }
      };

      // Set up click handler on marker element for selection
      markerElement.addEventListener("click", handleMarkerClick);
      markerElement.addEventListener("keypress", handleKeyPress);

      // Store property reference on the marker for later lookup
      (marker as mapboxgl.Marker & { _propertyId: string })._propertyId =
        property.id;

      // Add radius circles if radius information is available
      if (bamData.radius && Array.isArray(bamData.radius)) {
        bamData.radius.forEach(
          (_radiusItem: { value: string }, index: number) => {
            const radiusData = {
              radius: bamData?.radius,
              radiusLength: bamData.radiusLength,
              radiusColor: bamData.radiusColor,
              radiusLabel: bamData.radiusLabel,
            };
            addRadiusCircle(property, radiusData, index);
          },
        );
      }

      return marker;
    },
    [
      propertyImagesCache,
      surveyId,
      getMarkerDisplayContent,
      addRadiusCircle,
      hasAddressLine2Custom,
      currentSurvey?.sections,
    ],
  );

  // Create a square marker for a lease comp with custom color and optional radius rings
  const createLeaseCompMarker = useCallback(
    (leaseComp: LeaseComp, index: number) => {
      const coords = getLeaseCompCoordinates(leaseComp);
      if (!coords) return null;
      const [lng, lat] = coords;

      const iconData = getLeaseCompIconData(leaseComp);
      const markerElement = createLeaseCompMarkerElement(
        index,
        iconData.iconColor,
        iconData.iconSize,
      );
      markerElement.setAttribute("role", "button");
      markerElement.setAttribute("tabindex", "0");
      markerElement.setAttribute(
        "aria-label",
        `Lease Comp: ${leaseComp.name || leaseComp.leaseCompFields?.propertyName || ""}`,
      );

      const fields = leaseComp.leaseCompFields;
      const address = (fields?.propertyAddress ?? "").replace(/,+\s*$/, "");
      const hasLeaseCompFieldRules = leaseCompFieldRulesRef.current.length > 0;
      const isLeaseFieldActive = (keyName: string) =>
        leaseCompFieldRulesRef.current.some(
          (r) => r.keyName === keyName && r.isActive,
        );
      const lcCityVisible = hasLeaseCompFieldRules
        ? isLeaseFieldActive("propertyCity")
        : true;
      const lcStateVisible = hasLeaseCompFieldRules
        ? isLeaseFieldActive("propertyState")
        : true;
      const lcPostalCodeVisible = hasLeaseCompFieldRules
        ? isLeaseFieldActive("propertyPostalCode")
        : true;
      const stateZip = [
        lcStateVisible ? fields?.propertyState : null,
        lcPostalCodeVisible ? fields?.propertyPostalCode : null,
      ]
        .filter(Boolean)
        .join("  ");
      const addressLine = [
        lcCityVisible ? fields?.propertyCity : null,
        stateZip || null,
      ]
        .filter(Boolean)
        .join(", ");
      const imageSrc =
        leaseMediaFiles[leaseComp.leaseCompId]?.images?.[0]?.cloudfrontUrl ??
        "/image-placeholder-medium.png";

      // Build popup using DOM APIs to prevent XSS
      const showPropertyPhoto = currentSurvey?.showPropertyPhoto !== false;
      const isLeaseCompsSectionHidden =
        currentSurvey?.sections?.find((s) => s.name === "lease comps")
          ?.isHidden ?? false;
      const popupEl = document.createElement("div");
      popupEl.className = "popup-container";

      if (showPropertyPhoto) {
        const bgDiv = document.createElement("div");
        bgDiv.className = "popup-bg";
        if (/^(\/|https?:\/\/)/.test(imageSrc)) {
          bgDiv.style.backgroundImage = `url(${imageSrc})`;
        }
        popupEl.appendChild(bgDiv);
      }

      const contentDiv = document.createElement("div");
      contentDiv.className = "popup-content";

      const nameEl = document.createElement("h6");
      nameEl.textContent =
        leaseComp.name || fields?.propertyName || "Lease Comp";
      contentDiv.appendChild(nameEl);

      const addressEl = document.createElement("p");
      addressEl.textContent = [address, addressLine].filter(Boolean).join(", ");
      contentDiv.appendChild(addressEl);

      if (!isLeaseCompsSectionHidden) {
        const seeDetailsBtn = document.createElement("button");
        seeDetailsBtn.className = "popup-button";
        seeDetailsBtn.textContent = "See Details";
        seeDetailsBtn.addEventListener("click", () => {
          const safeSurveyId = encodeURIComponent(surveyId!);
          const safeLeaseCompId = encodeURIComponent(leaseComp.leaseCompId);
          const path = `/interactive/${safeSurveyId}/leasecomps?highlight=${safeLeaseCompId}`;
          const resolved = new URL(path, window.location.origin);
          window.location.assign(resolved.pathname + resolved.search);
        });
        contentDiv.appendChild(seeDetailsBtn);
      }

      popupEl.appendChild(contentDiv);

      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: true,
        closeOnClick: true,
        maxWidth: "260px",
        className: "property-popup lease-comp-popup",
      }).setDOMContent(popupEl);

      popup.on("open", () => {
        if (currentPopupRef.current && currentPopupRef.current !== popup) {
          currentPopupRef.current.remove();
        }
        currentPopupRef.current = popup;
      });

      popup.on("close", () => {
        if (currentPopupRef.current === popup) {
          currentPopupRef.current = null;
        }
      });

      if (lng == null || lat == null || isNaN(lng) || isNaN(lat)) {
        return null;
      }
      const marker = new mapboxgl.Marker(markerElement)
        .setLngLat([lng, lat])
        .setPopup(popup);

      const handleClick = () => {
        setSelectedProperty(null);
        setSelectedLeaseCompId(leaseComp.leaseCompId);
        // Popup toggle is handled by Mapbox's setPopup; currentPopupRef is set via popup.on("open")
      };

      markerElement.addEventListener("click", handleClick);
      markerElement.addEventListener("keypress", (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      });

      // Store lease comp ID on marker for sidebar click lookup
      (marker as mapboxgl.Marker & { _leaseCompId: string })._leaseCompId =
        leaseComp.leaseCompId;

      // Add radius circles if radius information is available in mapData
      if (
        iconData.radius &&
        Array.isArray(iconData.radius) &&
        iconData.radius.length > 0
      ) {
        // Create a property-like object for the radius circle utility
        const pseudoProperty = {
          id: `lc_${leaseComp.leaseCompId}`,
          latitude: lat,
          longitude: lng,
        } as Property;

        iconData.radius.forEach(
          (_radiusItem: { value: string | number }, rIndex: number) => {
            addRadiusCircle(
              pseudoProperty,
              {
                radius: iconData.radius,
                radiusLength: iconData.radiusLength,
                radiusColor: iconData.radiusColor,
                radiusLabel: iconData.radiusLabel,
              },
              rIndex,
            );
          },
        );
      }

      return marker;
    },
    [leaseMediaFiles, surveyId, currentSurvey, setSelectedLeaseCompId],
  );

  // Create a circle marker for a POI with a popup showing name and address
  const createPoiMarker = useCallback(
    (poi: POI) => {
      const coords = getPoiCoordinates(poi);
      if (!coords) return null;
      const [lng, lat] = coords;
      if (isNaN(lng) || isNaN(lat)) return null;

      const {
        iconColor,
        iconSize,
        iconName,
        pinType,
        logoUrl,
        logoWhiteBackground,
      } = getPoiIconData(poi);
      const markerElement = createPoiMarkerElement(
        iconColor,
        iconSize,
        iconName,
        pinType,
        logoUrl,
        logoWhiteBackground,
      );
      markerElement.setAttribute("role", "button");
      markerElement.setAttribute("tabindex", "0");
      markerElement.setAttribute(
        "aria-label",
        `Point of Interest: ${poi.name || ""}`,
      );

      // Build popup using DOM APIs to prevent XSS
      const popupEl = document.createElement("div");
      popupEl.className = "popup-container";

      const contentDiv = document.createElement("div");
      contentDiv.className = "popup-content";

      const nameEl = document.createElement("h6");
      nameEl.textContent =
        poi.category?.categoryName || poi.name || "Point of Interest";
      contentDiv.appendChild(nameEl);

      const popupAddress = getPoiAddress(poi);
      if (popupAddress) {
        const addressEl = document.createElement("p");
        addressEl.textContent = popupAddress;
        contentDiv.appendChild(addressEl);
      }

      const locationNotes = getPoiLocationNotes(poi);
      if (locationNotes) {
        const notesLabelEl = document.createElement("p");
        notesLabelEl.style.margin = "8px 0 2px";
        notesLabelEl.style.fontWeight = "600";
        notesLabelEl.style.fontSize = "0.75rem";
        notesLabelEl.style.color = "#374151";
        notesLabelEl.textContent = "Location Notes";
        contentDiv.appendChild(notesLabelEl);

        const notesValueEl = document.createElement("p");
        notesValueEl.style.margin = "0";
        notesValueEl.style.whiteSpace = "pre-wrap";
        notesValueEl.textContent = locationNotes;
        contentDiv.appendChild(notesValueEl);
      }

      popupEl.appendChild(contentDiv);

      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: true,
        closeOnClick: true,
        maxWidth: "260px",
        className: "property-popup poi-popup",
      }).setDOMContent(popupEl);

      popup.on("open", () => {
        if (currentPopupRef.current && currentPopupRef.current !== popup) {
          currentPopupRef.current.remove();
        }
        currentPopupRef.current = popup;
      });

      popup.on("close", () => {
        if (currentPopupRef.current === popup) {
          currentPopupRef.current = null;
        }
      });

      const marker = new mapboxgl.Marker(markerElement)
        .setLngLat([lng, lat])
        .setPopup(popup);

      markerElement.addEventListener("keypress", (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          const p = marker.getPopup();
          if (p && !p.isOpen()) p.addTo(map.current!);
        }
      });

      (marker as mapboxgl.Marker & { _poiId: string })._poiId = poi.poiId;

      return marker;
    },
    [currentSurvey],
  );

  const addMarkersToMap = () => {
    if (!map.current) return;

    clearMarkersWrapper();

    // Clear radius circles only for properties that are no longer in the filtered list
    const currentVisiblePropertyIds =
      filteredProperties.length > 0
        ? new Set(filteredProperties.map((p) => p.id))
        : new Set();

    const activeCirclePropertyIds = new Set(
      Array.from(radiusCirclesRef.current).map((circleId) => {
        const parts = circleId.split("_");
        return parts.slice(0, -1).join("_"); // Extract property ID properly using underscores
      }),
    );

    // Remove radius circles for properties that are no longer visible
    activeCirclePropertyIds.forEach((propertyId) => {
      if (!currentVisiblePropertyIds.has(propertyId)) {
        removeRadiusCircleWrapper(propertyId);
      }
    });

    // If no filtered properties AND no lease comps AND no POIs, clear any remaining radius circles and return
    if (
      filteredProperties.length === 0 &&
      filteredLeaseComps.length === 0 &&
      filteredPois.length === 0
    ) {
      clearAllRadiusCirclesWrapper();
      return;
    }

    // Add POI markers first (bottom z-order)
    poiMarkersRef.current.forEach((m) => m.remove());
    poiMarkersRef.current = [];
    filteredPois.forEach((poi) => {
      const marker = createPoiMarker(poi);
      if (marker) {
        marker.addTo(map.current!);
        poiMarkersRef.current.push(marker);
      }
    });

    // Add lease comp markers next so they render above POIs
    leaseCompMarkersRef.current.forEach((m) => m.remove());
    leaseCompMarkersRef.current = [];
    filteredLeaseComps.forEach((lc, index) => {
      const marker = createLeaseCompMarker(lc, index);
      if (marker) {
        marker.addTo(map.current!);
        leaseCompMarkersRef.current.push(marker);
      }
    });

    // Add property markers last so they appear above lease comp pins
    filteredProperties.forEach((property) => {
      const marker = createPropertyMarker(property);
      if (marker) {
        marker.addTo(map.current!);
        markersRef.current.push(marker);
      }
    });
  };

  // Stable string keys — useEffect compares strings by value, preventing spurious
  // marker re-draws when the lists haven't actually changed (e.g. popup opens).
  const filteredPropertyIds = filteredProperties.map((p) => p.id).join(",");
  const filteredLeaseCompIds = filteredLeaseComps
    .map((lc) => lc.leaseCompId)
    .join(",");
  const filteredPoiIds = filteredPois.map((p) => p.poiId).join(",");

  useEffect(() => {
    if (!map.current) return;
    map.current?.once("idle", () => {
      addMarkersToMap();
      if (map.current) {
        addMarinePortLayer(map.current, marinePorts || [], portsVisible);
      }
      renderPolygons();
    });
  }, [
    filteredPropertyIds,
    filteredLeaseCompIds,
    filteredPoiIds,
    createPropertyMarker,
    renderPolygons,
  ]);

  // Center map on properties
  const centerMapWrapper = useCallback(() => {
    centerMapUtil(
      map.current,
      filteredProperties,
      currentSurvey?.isIncludeMapKey || false,
      [
        ...filteredLeaseComps,
        ...filteredPois.map(
          (poi) =>
            ({
              leaseCompId: poi.poiId,
              leaseCompFields: {
                latitude: String(poi.mapData?.latitude ?? poi.latitude ?? ""),
                longitude: String(
                  poi.mapData?.longitude ?? poi.longitude ?? "",
                ),
              },
            }) as unknown as LeaseComp,
        ),
      ],
      polygons,
    );
  }, [filteredProperties, filteredLeaseComps, filteredPois, polygons]);

  // Trigger map resize when sidebar state changes
  useEffect(() => {
    if (map.current) {
      // Small delay to allow sidebar animation to complete
      setTimeout(() => {
        map.current!.resize();
      }, 300);
    }
  }, [sidebarOpen]);

  // Auto-center when filters change (properties, lease comps, or POIs)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        filteredProperties.length > 0 ||
        filteredLeaseComps.length > 0 ||
        filteredPois.length > 0 ||
        polygons.some((polygon) => !polygon.isHidden)
      ) {
        centerMapWrapper();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    filteredProperties.length,
    filteredLeaseComps.length,
    filteredPois.length,
    polygons,
    centerMapWrapper,
  ]);

  const handleChangeMapStyle = (
    _event?: React.ChangeEvent<Element>,
    _name?: string,
    value?: string | number,
  ) => {
    const selectedStyle = MAP_STYLES.find((style) => style.id === value);
    if (selectedStyle?.id === "emeraldBingHybrid") {
      selectedStyle.url = currentBingMap || "";
    }
    if (selectedStyle) {
      map.current?.setStyle(selectedStyle.url);
      setCurrentMapStyle(selectedStyle);
    }
    map.current?.once("idle", () => {
      if (map.current) {
        deleteMarkers(map.current);
      }
      addMarkersToMap();
      if (map.current) {
        addMarinePortLayer(map.current, marinePorts || [], portsVisible);
      }
      renderPolygons();
    });
  };

  const handleMarinePortsChange = (checked: boolean) => {
    setPortsVisible(checked);
    if (!map.current) return;

    const addLayer = () => {
      if (map.current) {
        addMarinePortLayer(map.current, marinePorts || [], checked);
      }
    };

    if (map.current.isStyleLoaded()) {
      addLayer();
    } else {
      map.current.once("idle", addLayer);
    }
  };

  return (
    <div className="flex flex-col bg-gray-50 h-full">
      {/* Error State */}
      {loadingState.error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-4 mt-4">
          <div className="flex">
            <div className="shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading map
              </h3>
              <p className="text-sm text-red-700 mt-1">{loadingState.error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Map and Sidebar Container */}
      <div className="map-container">
        {/* Map Container */}
        <div className="flex-1 relative">
          <div ref={mapContainer} className="map" />

          {currentSurvey?.isIncludeMapKey && <MapKey />}
          {/* Center Map Button */}
          <button
            onClick={centerMapWrapper}
            disabled={
              filteredProperties.length === 0 &&
              filteredLeaseComps.length === 0 &&
              filteredPois.length === 0
            }
            className="map-control map-control-top"
            title="Center map on properties"
          >
            <CenterImg />
          </button>

          {/* Map Style Selector */}
          <EmeraldPopover
            className="map-style-popover"
            placement="bottom-start"
            closeOnOutsideClick
            showCloseIcon
            triggerElement={
              <MapSettingsIcon
                className={`absolute right-2.5 bottom-8.5 md:bottom-8.5 max-md:bottom-16 rounded-sm bg-white z-1 p-[2.5px] text-default border-2 border-gray shadow-[0_1px_5px_0_rgba(26,26,26,0.2)] ${
                  loadingState.map
                    ? "map-settings-icon-loading"
                    : "map-settings-icon-default"
                }`}
                width={36}
                height={36}
              />
            }
          >
            <div className="text-default font-body">
              <div className="sticky top-0 bg-white z-10 rounded-sm">
                <h2 className="text-sm font-body! font-semibold! text-default! pb-2 pl-4">
                  Map Style
                </h2>
              </div>

              <div className="max-h-50 overflow-y-auto">
                <EmeraldRadioGroup
                  defaultValue={currentMapStyle?.id}
                  onChange={handleChangeMapStyle}
                  name="mapStyle"
                >
                  {MAP_STYLES.map((style) => (
                    <EmeraldRadio
                      key={style.id}
                      value={style.id}
                      label={style.name}
                      disabled={loadingState.map}
                    />
                  ))}
                </EmeraldRadioGroup>
              </div>
            </div>
          </EmeraldPopover>

          {/* Map layers */}
          {currentSurvey?.isMarinePorts && (
            <EmeraldPopover
              className="map-layers-popover"
              placement="bottom-start"
              closeOnOutsideClick
              showCloseIcon
              triggerElement={
                <EmeraldIcon
                  icon="layers"
                  type="filled"
                  color={loadingState.map ? "#CCCCCC" : "#5A5A5A"}
                  fontsizeInRem={1.69}
                  variant="symbol"
                  className="shadow-[0_1px_5px_0_rgba(26,26,26,0.2)] absolute right-2.5 bottom-19.5 md:bottom-19.5 max-md:bottom-27 rounded-sm bg-white z-1 p-[2.5px] text-default border-2 border-gray"
                />
              }
            >
              <div className="text-default font-body">
                <div className="sticky top-0 bg-white z-10 rounded-sm">
                  <h2 className="text-sm font-body! font-semibold! text-default! pb-2 px-4">
                    Map Layers
                  </h2>
                </div>

                <div className="max-h-50 overflow-y-auto pl-1.5 pr-4">
                  <EmeraldCheckbox
                    checked={portsVisible}
                    label="Marine Ports"
                    onChange={handleMarinePortsChange}
                  />
                </div>
              </div>
            </EmeraldPopover>
          )}

          {(loadingState.map || loadingState.properties) && (
            <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex items-center justify-center">
              <div className="text-center bg-white p-6 rounded-lg shadow-lg">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">
                  {loadingState.map
                    ? `Loading ${currentMapStyle.name.toLowerCase()} map style...`
                    : "Loading properties..."}
                </p>
                {loadingState.map && (
                  <p className="text-xs text-gray-500 mt-2">
                    Preserving current view and restoring property markers
                  </p>
                )}
              </div>
            </div>
          )}

          {/* No Properties Message */}
          {!loadingState.properties &&
            filteredProperties.length === 0 &&
            filteredLeaseComps.length === 0 &&
            filteredPois.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                  <svg
                    className="w-12 h-12 text-gray-400 mx-auto mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Properties Found
                  </h3>
                  <p className="text-gray-600">
                    No properties are available with the current settings.
                  </p>
                </div>
              </div>
            )}
        </div>

        {/* Property Sidebar */}
        <PropertySidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          filteredProperties={sidebarProperties}
          properties={properties}
          selectedProperty={selectedProperty}
          selectedLeaseCompId={selectedLeaseCompId}
          loadingState={loadingState}
          handleLeaseCompClick={handleLeaseCompClick}
          handlePropertyClick={handlePropertyClick}
          updateFilter={updateFilter}
        />

        {/* Property Images Cache - Hidden component for pre-loading images */}
        <PropertyImagesCache
          properties={sidebarProperties}
          onImagesResolved={setPropertyImagesCache}
        />
      </div>
    </div>
  );
};

export default InteractiveSurveyMarketMap;
